
import React, { useState } from 'react';
import { DetailedBotAccount } from '@/types/trader';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ChevronDown, Trash2, ToggleLeft, ToggleRight, Copy, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { AccountDetails } from '@/components/accounts/trader-card/AccountDetails';
import { AccountTypeBadge } from '@/components/common/AccountTypeBadge';
import { CTraderLogo } from '@/components/accounts/ctrader-card/CTraderLogo';
import { EnhancedAccountBalance } from '@/components/accounts/trader-card/EnhancedAccountBalance';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from '@/hooks/use-mobile';
import { useToast } from '@/hooks/use-toast';

interface EnhancedPremiumTradingAccountCardProps {
  account: DetailedBotAccount;
  onToggleConnection: (accountId: string, isConnected: boolean) => void;
  onDeleteAccount: (accountId: string) => void;
  isProcessing?: boolean;
  isDeleting?: boolean;
}

export const EnhancedPremiumTradingAccountCard = ({
  account,
  onToggleConnection,
  onDeleteAccount,
  isProcessing = false,
  isDeleting = false,
}: EnhancedPremiumTradingAccountCardProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const { toast } = useToast();
  
  const isConnected = account.status === 'connected';
  const isLive = account.trading_account?.live || false;
  const tradingAccountNumber = account.trading_account?.number_account_trading || "Unknown";
  const accountBalance = account.trading_account?.balance_account || 0;
  const brokerOrPropFirm = account.csp_account?.broker || account.csp_account?.prop_firm || 'CTrader';
  const isReverse = account.is_reverse_position;

  const copyAccountNumber = () => {
    navigator.clipboard.writeText(tradingAccountNumber);
    toast({
      title: t('copied_to_clipboard'),
      description: t('account_number_copied')
    });
  };

  const handleToggleConnection = async () => {
    try {
      await onToggleConnection(account.id, !isConnected);
    } catch (error) {
      console.error('Error toggling connection:', error);
    }
  };

  const handleDelete = async () => {
    try {
      // Close dialog immediately for better UX
      setIsDialogOpen(false);
      await onDeleteAccount(account.id);
    } catch (error) {
      console.error('Error deleting account:', error);
    }
  };

  // Apply fade-out animation when account is being deleted
  const cardClassName = `overflow-hidden border hover:border-primary/30 hover:shadow-lg transition-all duration-300 bg-gradient-to-br from-background to-background/50 ${
    isDeleting ? 'opacity-50 pointer-events-none animate-pulse' : ''
  }`;

  return (
    <Collapsible
      open={isOpen}
      onOpenChange={setIsOpen}
      className={`w-full transition-all duration-300 ${isDeleting ? 'scale-95 opacity-50' : 'hover:scale-[1.02]'}`}
    >
      <Card className={cardClassName}>
        <div className={`${isMobile ? 'p-4' : 'p-5'}`}>
          <CollapsibleTrigger asChild className="w-full">
            <div className="flex justify-between items-start cursor-pointer group">
              <div className="flex items-center gap-3 flex-1">
                <CTraderLogo platformName={brokerOrPropFirm} />
                <div className="flex flex-col min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className={`${isMobile ? 'text-base' : 'text-lg'} font-bold font-mono tracking-wider text-foreground group-hover:text-primary transition-colors`}>
                      {tradingAccountNumber}
                    </span>
                    {!isMobile && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={(e) => {
                          e.stopPropagation();
                          copyAccountNumber();
                        }}
                        disabled={isDeleting}
                      >
                        <Copy className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                  <span className={`${isMobile ? 'text-xs' : 'text-sm'} text-muted-foreground font-medium truncate`}>
                    {account.csp_account?.csp_account_name || t('unknown')}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <AccountTypeBadge isLive={isLive} size={isMobile ? 'sm' : 'md'} />
                </div>
                <div className="relative">
                  <Badge 
                    variant={isConnected ? "default" : "outline"} 
                    className={`text-xs h-5 transition-all duration-200 ${
                      isConnected 
                        ? 'bg-green-500/20 text-green-700 hover:bg-green-500/30 border-green-500/30' 
                        : 'bg-gray-100 text-gray-600 border-gray-300'
                    }`}
                  >
                    {isConnected ? (
                      <>
                        <div className="w-2 h-2 bg-green-500 rounded-full mr-1 animate-pulse" />
                        {t('connected')}
                      </>
                    ) : (
                      t('disconnected')
                    )}
                  </Badge>
                </div>
                <ChevronDown 
                  className={`${isMobile ? 'h-3 w-3' : 'h-4 w-4'} transition-all duration-300 text-muted-foreground group-hover:text-primary ${isOpen ? 'rotate-180' : ''}`} 
                />
              </div>
            </div>
          </CollapsibleTrigger>

          {/* Balance and Reverse Position Row */}
          <div className={`flex justify-between items-center gap-3 ${isMobile ? 'mt-3' : 'mt-4'}`}>
            <EnhancedAccountBalance balance={accountBalance} size={isMobile ? 'sm' : 'md'} />
            
            {isReverse && (
              <div className="flex items-center gap-1.5 bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/20 rounded-lg px-2 py-1 flex-shrink-0 max-w-[120px]">
                <div className="flex items-center justify-center w-4 h-4 rounded-full bg-amber-500/20">
                  <RotateCcw className="w-2.5 h-2.5 text-amber-600" />
                </div>
                <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-500/30 text-[10px] px-1 py-0 h-4 min-w-0">
                  REV
                </Badge>
              </div>
            )}
          </div>

          <CollapsibleContent className="transition-all duration-300 ease-in-out">
            <div className={`${isMobile ? 'mt-4 pt-3' : 'mt-6 pt-4'} border-t border-border/60`}>
              <AccountDetails
                brokerOrPropFirm={brokerOrPropFirm}
                volumeMultiplier={account.volume_multiplier?.toString() || '1.0'}
                apiStatus={account.api_connection?.status || 'Unknown'}
                addedDate={account.added_date}
                createdAt={account.created_at || ''}
              />

              <div className={`flex items-center justify-between ${isMobile ? 'gap-2 mt-4' : 'gap-3 mt-6'}`}>
                <button
                  type="button"
                  onClick={handleToggleConnection}
                  className={`flex items-center ${isMobile ? 'px-3 py-2' : 'px-4 py-2.5'} rounded-lg focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-200 ${isMobile ? 'text-xs' : 'text-sm'} font-medium gap-2 shadow-sm
                    ${isConnected 
                      ? 'bg-primary/15 border border-primary/30 text-primary hover:bg-primary/20' 
                      : 'bg-muted hover:bg-muted/80 border border-muted-foreground/20 text-muted-foreground hover:text-foreground'}
                  `}
                  disabled={isProcessing || isDeleting}
                  aria-pressed={isConnected}
                >
                  {isConnected ? (
                    <>
                      <ToggleRight className={`${isMobile ? 'w-4 h-4' : 'w-5 h-5'}`} />
                      {!isMobile && <span>{t('disconnect')}</span>}
                      {isMobile && <span>Disconnect</span>}
                    </>
                  ) : (
                    <>
                      <ToggleLeft className={`${isMobile ? 'w-4 h-4' : 'w-5 h-5'}`} />
                      {!isMobile && <span>{t('reconnect')}</span>}
                      {isMobile && <span>Reconnect</span>}
                    </>
                  )}
                </button>

                <AlertDialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                  <AlertDialogTrigger asChild>
                    <Button 
                      variant="ghost" 
                      size="icon"
                      className={`${isMobile ? 'h-8 w-8' : 'h-10 w-10'} text-destructive hover:bg-destructive/10 hover:text-destructive border border-transparent hover:border-destructive/20 transition-all duration-200`}
                      disabled={isProcessing || isDeleting}
                    >
                      <Trash2 className={`${isMobile ? 'h-3 w-3' : 'h-4 w-4'}`} />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent className={isMobile ? 'max-w-[95vw]' : ''}>
                    <AlertDialogHeader>
                      <AlertDialogTitle>{t('delete_trading_account')}</AlertDialogTitle>
                      <AlertDialogDescription>
                        {t('delete_account_confirmation')}
                        <br />
                        <span className={`font-mono ${isMobile ? 'text-xs' : 'text-sm'} bg-muted px-2 py-1 rounded mt-2 inline-block`}>
                          {tradingAccountNumber}
                        </span>
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter className={isMobile ? 'flex-col gap-2' : ''}>
                      <AlertDialogCancel className={isMobile ? 'w-full' : ''}>{t('cancel')}</AlertDialogCancel>
                      <AlertDialogAction 
                        onClick={handleDelete}
                        className={`bg-destructive text-destructive-foreground hover:bg-destructive/90 ${isMobile ? 'w-full' : ''}`}
                      >
                        {t('delete')}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          </CollapsibleContent>
        </div>
      </Card>
    </Collapsible>
  );
};
